import React, { useState } from "react";
import {
  Avatar, Box, Chip, IconButton, Link, ListItemIcon, ListItemText, Menu, MenuItem, Stack, TableCell, TableRow, Tooltip, Typography,
} from "@mui/material";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import ClassOutlinedIcon from "@mui/icons-material/ClassOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Row } from "../types";

const PALETTE = [
  ["#dbeafe", "#1d4ed8"], ["#dcfce7", "#166534"], ["#fef3c7", "#92400e"],
  ["#f3e8ff", "#6b21a8"], ["#ffe4e6", "#9f1239"], ["#e0f2fe", "#075985"],
];
function stableAvatar(name: string) {
  const trimmed = (name || "?").trim();
  const initials = trimmed.split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase() || "").join("") || "?";
  let hash = 0;
  for (let i = 0; i < trimmed.length; i++) hash = (hash * 31 + trimmed.charCodeAt(i)) >>> 0;
  const [bg, color] = PALETTE[hash % PALETTE.length];
  return { initials, bg, color };
}

const gradeLabel = (g: string | null) => {
  if (!g) return "—";
  if (/^\d+$/.test(g)) return `Grade ${g}`;
  return g;
};

const modeLabel = (m: string | null) => {
  if (!m) return "—";
  const v = m.toLowerCase();
  if (v === "online") return "Online";
  if (v === "offline" || v === "in_person") return "In Person";
  if (v === "hybrid") return "Hybrid";
  return m;
};

function to12h(t: string | null): string {
  if (!t) return "";
  const m = /^(\d{1,2}):(\d{2})/.exec(t);
  if (!m) return t;
  let h = Number(m[1]); const mm = m[2];
  const suffix = h >= 12 ? "PM" : "AM";
  h = h % 12; if (h === 0) h = 12;
  return `${h}:${mm} ${suffix}`;
}

function formatDate(d: string | null): string | null {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

const statusChip = (s: string | null) => {
  if (!s) return <Chip size="small" label="—" />;
  const normalized = s === "Added" ? "Active" : s;
  const color: Record<string, any> = {
    Active: "success", Future: "info", Ended: "default",
    Released: "warning", Removed: "error", Cancelled: "error",
  };
  return <Chip size="small" color={color[normalized] ?? "default"} label={normalized} />;
};

type Props = {
  row: Row;
  onViewStudent: (id: number) => void;
  onViewClass: (id: number) => void;
  onViewStaff: (id: number) => void;
  /**
   * Opens the shared AssignmentDetailsModal with the full grade/subject/staff
   * matrix for this row. Wiring this at the parent lets the same modal open
   * from the Class column title, the compact "+more" summary, and the row
   * actions menu — all pointing at the same detail surface.
   */
  onOpenDetails: (row: Row) => void;
};

export default function ClassRow({ row, onViewStudent, onViewClass, onViewStaff, onOpenDetails }: Props) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const avatar = stableAvatar(row.student_name || "?");

  const gradeCount = row.class_grades.length;
  const subjectCount = row.class_subjects.length;
  // One-line class summary — the crowded chip wall used to live here, but
  // scanning 12 chips per row was noisy. Full detail lives in the modal.
  const classSummary = [
    gradeCount > 0 ? `${gradeCount} grade${gradeCount === 1 ? "" : "s"}` : null,
    subjectCount > 0 ? `${subjectCount} subject${subjectCount === 1 ? "" : "s"}` : null,
  ].filter(Boolean).join(" • ");
  const classTitle = row.class_name || `Class ${row.class_id}`;

  return (
    <TableRow hover sx={{ verticalAlign: "top", "&:nth-of-type(even)": { background: "#fafafa" } }}>
      {/* Student */}
      <TableCell>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar sx={{ bgcolor: avatar.bg, color: avatar.color, width: 34, height: 34, fontSize: 13 }}>
            {avatar.initials}
          </Avatar>
          <Box minWidth={0}>
            <Link component="button" underline="hover"
              onClick={() => onViewStudent(row.student_id)}
              sx={{ fontWeight: 600, color: "#1b3a6b", textAlign: "left" }}>
              {row.student_name || "—"}
            </Link>
            <Typography variant="caption" color="text.secondary" display="block">
              ID {row.reference_id || row.student_id}{row.school_grade ? ` · ${gradeLabel(row.school_grade)}` : ""}
            </Typography>
          </Box>
        </Stack>
      </TableCell>

      {/* Class — summary-first: title + ID + one-line grade/subject count + View details */}
      <TableCell sx={{ maxWidth: 260 }}>
        <Tooltip title={classTitle} placement="top" arrow enterDelay={400} disableHoverListener={classTitle.length < 32}>
          <Link
            component="button"
            underline="hover"
            onClick={() => onOpenDetails(row)}
            sx={{
              fontWeight: 700, color: "#1b3a6b", fontSize: 13.5, textAlign: "left",
              display: "block", width: "100%",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}
          >
            {classTitle}
          </Link>
        </Tooltip>
        <Typography variant="caption" color="text.secondary" display="block">
          Class ID {row.class_code || row.class_id}
        </Typography>
        {classSummary && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
            {classSummary}
          </Typography>
        )}
        <Link
          component="button"
          underline="hover"
          onClick={() => onOpenDetails(row)}
          sx={{ fontSize: 11.5, fontWeight: 600, color: "#2563eb", mt: 0.5, display: "inline-block" }}
        >
          View details
        </Link>
      </TableCell>

      {/* Grade & Subject — compact preview only, "+N more" opens the modal */}
      <TableCell sx={{ maxWidth: 220 }}>
        <Stack spacing={0.5}>
          {gradeCount > 0 && (
            <Box>
              {row.class_grades.slice(0, 2).map((g) => (
                <Chip key={g} size="small" variant="outlined"
                  label={gradeLabel(g)}
                  sx={{ mr: 0.5, mb: 0.25, borderColor: "#c7d2fe", color: "#1b3a6b", fontWeight: 600 }} />
              ))}
              {gradeCount > 2 && (
                <Link
                  component="button" underline="hover"
                  onClick={() => onOpenDetails(row)}
                  sx={{ fontSize: 11.5, fontWeight: 600, color: "#64748b" }}
                >
                  +{gradeCount - 2} more
                </Link>
              )}
            </Box>
          )}
          {subjectCount > 0 && (
            <Box>
              {row.class_subjects.slice(0, 2).map((s, i) => (
                <Chip key={i} size="small" label={s}
                  sx={{ mr: 0.5, mb: 0.25, background: "#eff6ff", color: "#1b3a6b", fontWeight: 500, maxWidth: 160,
                    "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" } }} />
              ))}
              {subjectCount > 2 && (
                <Link
                  component="button" underline="hover"
                  onClick={() => onOpenDetails(row)}
                  sx={{ fontSize: 11.5, fontWeight: 600, color: "#64748b" }}
                >
                  +{subjectCount - 2} more
                </Link>
              )}
            </Box>
          )}
          {gradeCount === 0 && subjectCount === 0 && (
            <Typography variant="caption" color="text.secondary">—</Typography>
          )}
        </Stack>
      </TableCell>

      {/* Staff — name + location only. Full role/matrix lives in the modal. */}
      <TableCell sx={{ maxWidth: 180 }}>
        {row.staff_name ? (
          <Link component="button" underline="hover"
            onClick={() => row.staff_id && onViewStaff(row.staff_id)}
            sx={{
              fontWeight: 600, color: "#1b3a6b", textAlign: "left",
              display: "block", width: "100%",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
            {row.staff_name}
          </Link>
        ) : <span>—</span>}
        {row.location && (
          <Typography variant="caption" color="text.secondary" display="block"
            sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {row.location}
          </Typography>
        )}
      </TableCell>

      {/* Schedule */}
      <TableCell>
        <Typography variant="body2" fontWeight={600}>{row.day || "—"}</Typography>
        <Typography variant="caption" color="text.secondary">
          {row.start_time ? `${to12h(row.start_time)}–${to12h(row.end_time)}` : "—"}
        </Typography>
      </TableCell>

      {/* Mode & Location */}
      <TableCell>
        <Chip size="small" variant="outlined" label={modeLabel(row.study_mode)}
          sx={{ borderColor: "#e2e8f0", mb: 0.5 }} />
        <Stack direction="row" spacing={0.5} alignItems="center">
          <LocationOnOutlinedIcon fontSize="inherit" sx={{ color: "#64748b" }} />
          <Typography variant="caption" color="text.secondary">
            {row.location || <em style={{ color: "#94a3b8" }}>Not assigned</em>}
          </Typography>
        </Stack>
      </TableCell>

      {/* Assignment */}
      <TableCell>
        {!row.start_date && !row.end_date ? (
          <Typography variant="caption" color="text.secondary">No date restriction</Typography>
        ) : (
          <Stack spacing={0.25}>
            <Typography variant="caption">
              Started {formatDate(row.start_date) || "—"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.end_date ? `Ends ${formatDate(row.end_date)}` : "Ongoing"}
            </Typography>
          </Stack>
        )}
      </TableCell>

      {/* Status */}
      <TableCell>{statusChip(row.assignment_status)}</TableCell>

      {/* Actions */}
      <TableCell align="right">
        <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
          <MoreVertOutlinedIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
          <MenuItem onClick={() => { setMenuAnchor(null); onOpenDetails(row); }}>
            <ListItemIcon><VisibilityOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="View Assignment Details" />
          </MenuItem>
          <MenuItem onClick={() => { setMenuAnchor(null); onViewStudent(row.student_id); }}>
            <ListItemIcon><PersonOutlineOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="View Student" />
          </MenuItem>
          <MenuItem onClick={() => { setMenuAnchor(null); onViewClass(row.class_id); }}>
            <ListItemIcon><ClassOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="View Class Details" />
          </MenuItem>
          {row.staff_id && (
            <MenuItem onClick={() => { setMenuAnchor(null); onViewStaff(row.staff_id!); }}>
              <ListItemIcon><BadgeOutlinedIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="View Staff" />
            </MenuItem>
          )}
          <MenuItem onClick={() => { setMenuAnchor(null); onViewClass(row.class_id); }}>
            <ListItemIcon><AssignmentOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Open Assignment" />
          </MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
}
